// packages/core/src/learning/school-modules/cashflow-assets.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'cashflow-assets' school. LearningModule, surface 'school'.
//   module id   = 'school-cashflow-assets'  (matches exhibit nativeModuleId)
//   homeSchool  = 'cashflow-assets'
// PROMOTED: three authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_CASHFLOW_ASSETS_MODULE: LearningModule = {
	id: 'school-cashflow-assets',
	title: 'Cashflow & Assets',
	description:
		'The lens that judges ownership by usable income — net cash after costs, the margin a bad month demands, and whether cash flow buys real options.',
	emoji: '🏠',
	accentColor: '#5499e0',
	tier: 'growth',
	pillar: 'long-term-investing',
	order: 0,
	surface: 'school',
	homeSchool: 'cashflow-assets',
	lessons: [
		{
			id: 'school-cashflow-assets-01',
			title: 'Cash flow is not the headline number',
			description:
				'This lesson teaches why cashflow assets judges income after costs, friction, and bad months.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['cashflow-assets'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['cashflow-assets', 'net-income', 'gross-income-trap'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'net income reality',
				'gross income trap',
				'cash flow friction',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'A rental, small business, vending machine, dividend portfolio, or digital product can all sound powerful when someone only says the monthly income number. The cashflow assets lens slows down and asks what is left after the real world gets paid.',
				},
				{
					type: 'paragraph',
					text: 'This school sees assets through usable income. It is not impressed by the headline check. It cares about net cash, consistency, and how much attention the asset demands.',
				},
				{
					type: 'bullet_list',
					items: [
						'Gross income is the starting number',
						'Expenses decide what survives',
						'Bad months reveal the truth',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is calling income passive before subtracting repairs, fees, taxes, vacancies, mistakes, and your own time.',
				},
				{
					type: 'example',
					text: 'Someone says a rental brings in $2,200 a month. After the mortgage, insurance, property tax, repairs, vacancy savings, and management friction, the real monthly cash might be $250. That does not make it bad. It just makes the story more honest.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can become too focused on monthly checks. Some assets produce little cash flow today but may still build wealth through growth, simplicity, or diversification. Cash flow is useful, but it is not the only scoreboard.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lesson connects to your Dashboard because your own monthly surplus is the real baseline. Before judging any asset idea, compare it against your current income, spending, runway, and stress level.',
				},
				{
					type: 'key_takeaway',
					text: 'Cash flow only becomes real after the hidden costs are counted.',
				},
			],
			audioScript:
				'The cashflow assets lens starts with a simple warning: do not fall in love with the headline number. When someone says an asset makes two thousand dollars a month, that sounds clean. It sounds like freedom. It sounds like money arriving while you sleep. But this school is not asking, “What did the asset collect?” It is asking, “What did the asset keep after the real world showed up?”\n\nThink about a rental property. The rent might be $2,200 a month. That is the number people love to say out loud. But then the mortgage gets paid. Property tax shows up. Insurance gets paid. Repairs happen. A tenant moves out. A month sits empty. Maybe you hire a manager. Maybe you do it yourself, which still costs time, attention, phone calls, and stress. Suddenly the asset does not feel like a magic money printer. It feels like a small operating business that happens to be attached to a building.\n\nThat does not mean the rental is bad. This is important. Cashflow assets is not anti real estate, anti dividends, anti business, or anti ownership. It is anti fantasy. The lens wants you to respect the gap between gross income and usable income. Gross income is the loud number. Usable income is the number that changes your life.\n\nThe same idea applies outside real estate. A small online product might bring in $800 a month, but ads, software, refunds, taxes, customer support, and updates may eat part of that. A vending machine route might sound simple until you count gas, restocking, broken machines, stale inventory, and the time spent driving around. A dividend portfolio might send cash, but taxes, concentration, and lower growth can still matter. The check is only one part of the truth.\n\nThis lens protects you from passive income marketing. A lot of people sell the dream without showing the maintenance. Cashflow assets says, “Fine, show me the full picture.” What comes in? What goes out? What breaks? What happens when things are slower than expected? How much cash do you need in reserve so one bad month does not turn the asset into panic?\n\nThe blind spot is that cash flow can become its own obsession. Some people start treating every low cash flow asset like it is useless. That is too narrow. An asset can be valuable because it grows, diversifies, protects purchasing power, or stays simple. Monthly income is useful, but it is not the only valid form of progress.\n\nSo the practical move is not to worship cash flow. It is to make the income story honest. Before you admire the check, subtract the costs. Before you call something passive, count the work. Before you call it freedom, ask whether it actually gives you more options or just gives you another responsibility.',
			flashcards: [
				{
					id: 'school-cashflow-assets-01-fc-1',
					role: 'main_idea',
					front: 'What is real cash flow?',
					back: 'Money left after costs, reserves, taxes, friction, and bad months.',
				},
				{
					id: 'school-cashflow-assets-01-fc-2',
					role: 'watch_out',
					front: 'What is the gross income trap?',
					back: 'Mistaking the headline income number for money you actually keep.',
				},
				{
					id: 'school-cashflow-assets-01-fc-3',
					role: 'use_it',
					front: 'How do you test cash flow?',
					back: 'Compare the real net income against your current monthly picture.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Check your monthly picture',
			},
			revision: 1,
		},
		{
			id: 'school-cashflow-assets-02',
			title: 'Income assets still need a margin',
			description:
				'This lesson teaches why cashflow assets wants room for errors, delays, repairs, and slower months.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['cashflow-assets'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['cashflow-assets', 'income-margin', 'reserves'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'income margin',
				'bad month planning',
				'reserve thinking',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Cash flow feels safe because it arrives on a schedule. But the schedule can break. Tenants leave, customers disappear, rates change, repairs happen, platforms shift, and bills do not wait for your confidence to return.',
				},
				{
					type: 'paragraph',
					text: 'Cashflow assets is paying attention to margin. The question is not only whether an asset works in a good month. The question is whether it survives a rough month without forcing panic.',
				},
				{
					type: 'bullet_list',
					items: [
						'Keep room for repairs',
						'Expect slower income periods',
						'Separate cash flow from safety',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is spending every dollar of new cash flow as if the asset will never disappoint you.',
				},
				{
					type: 'example',
					text: 'A side business sends $600 a month for six months. If you immediately build your lifestyle around that full $600, one bad month becomes stress. If part of that cash builds reserves first, the income becomes more stable and useful.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that margin slows down the exciting part. Keeping reserves can feel boring when you want the asset to change your life right now. But cash flow with no cushion is fragile.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to runway. If a cash flowing asset has uneven income, your personal runway decides whether that unevenness feels like opportunity or chaos.',
				},
				{
					type: 'key_takeaway',
					text: 'Cash flow is stronger when it has room to disappoint you.',
				},
			],
			audioScript:
				'Cashflow assets sounds attractive because the money feels regular. Monthly rent. Monthly distributions. Monthly business profit. Monthly royalties. Monthly checks feel calming because they create a rhythm. But the real world does not care about your spreadsheet rhythm. Income can pause. Costs can jump. A customer can leave. A tenant can move out. A platform can change its rules. A repair can hit right when you thought the asset was finally working.\n\nThat is why this school cares about margin. Margin means room. Room for the asset to be less perfect than you hoped. Room for a slower month. Room for a repair. Room for a delay. Room for taxes. Room for a mistake in your assumptions.\n\nA cash flowing asset without margin can become fragile fast. Imagine a person buys something that produces $500 a month after basic costs. That sounds great. But if they immediately use the full $500 to upgrade their lifestyle, they have turned the asset into a promise. Now the asset has to keep performing. If the income drops to zero for one month, they are not just disappointed. They are short. That is a different emotional experience.\n\nNow imagine the same $500, but the first part goes toward reserves. Maybe not forever, but at least until there is a cushion. Now a bad month does not create the same panic. The asset can breathe. The owner can think. That extra room changes the feeling of the whole plan.\n\nThis applies to more than rental properties. A freelance income stream needs margin because clients can pay late. A small business needs margin because sales can dip. A dividend strategy needs margin because payouts can change and markets can fall. A creator product needs margin because attention can disappear. Cash flow is useful, but it is not a force field.\n\nThe mistake this lens warns against is spending the check too early in your mind. People often count new income before it has proven itself. They build plans around perfect months. They forget that every income asset has some version of maintenance. Sometimes that maintenance is physical. Sometimes it is emotional. Sometimes it is financial. Sometimes it is just the attention required to keep the thing from drifting.\n\nThe blind spot is that too much margin can make you overly cautious. If you demand impossible safety, you may never test anything. Cashflow assets should not turn into fear of ownership. It should turn into respect for the downside.\n\nInside Nisk, the useful question is simple: how much runway do you have if this income gets weird? If your personal runway is strong, uneven cash flow may feel manageable. If your runway is thin, the same asset may feel stressful even if the numbers look good. The asset does not exist alone. It sits inside your life.',
			flashcards: [
				{
					id: 'school-cashflow-assets-02-fc-1',
					role: 'main_idea',
					front: 'Why does cash flow need margin?',
					back: 'Because income can slow while costs still arrive.',
				},
				{
					id: 'school-cashflow-assets-02-fc-2',
					role: 'watch_out',
					front: 'What makes cash flow fragile?',
					back: 'Spending the full check before reserves and bad months are counted.',
				},
				{
					id: 'school-cashflow-assets-02-fc-3',
					role: 'use_it',
					front: 'What should runway reveal?',
					back: 'Whether uneven asset income would feel manageable or chaotic.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
			revision: 1,
		},
		{
			id: 'school-cashflow-assets-03',
			title: 'Cash flow can buy options',
			description:
				'This lesson teaches how cashflow assets connects recurring income to freedom, while staying honest about risk.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['cashflow-assets'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['cashflow-assets', 'option-income', 'cash-flow-tradeoff'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'option income',
				'ownership pressure',
				'cash flow tradeoff',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'The dream of cash flow is not just having more money. It is having income that gives your life more room. A little recurring surplus can make choices feel less trapped.',
				},
				{
					type: 'paragraph',
					text: 'Cashflow assets sees ownership as a way to create option income. That means money that may help cover bills, reduce pressure, fund experiments, or protect your time.',
				},
				{
					type: 'bullet_list',
					items: [
						'More monthly room',
						'Less pressure from one paycheck',
						'More choices during change',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating cash flow as automatic freedom. Bad assets can steal time, focus, and sleep.',
				},
				{
					type: 'example',
					text: 'An asset that reliably clears $300 a month might help someone fund a class, lower dependence on overtime, or build reserves faster. But if that $300 requires constant stress, surprise costs, or too much concentration, the freedom may be smaller than it looks.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is control versus simplicity. Cash flowing assets can feel more active and concrete, but they can also concentrate risk and demand attention. Broad, simple investing may feel less exciting, but it can be easier to live with.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, test the idea by simulating extra monthly cash. Do not ask only whether the asset sounds attractive. Ask whether the realistic net income would actually change your timeline, runway, or choices.',
				},
				{
					type: 'key_takeaway',
					text: 'The best cash flow is not just income. It is income that gives you more options than it takes away.',
				},
			],
			audioScript:
				'The strongest version of the cashflow assets lens is not “get rich from passive income.” That version is too clean, too online, and usually missing half the story. The stronger version is this: recurring income can buy options when it is real, durable enough, and not secretly costing you your peace.\n\nOptions are the heart of this lens. If an asset clears a realistic $300 a month, that money might not make you rich. But it might help you build runway faster. It might cover a bill. It might reduce overtime pressure. It might let you take a class, test a small business idea, or survive a messy month with less fear. That is why cash flow can feel powerful. It turns ownership into monthly breathing room.\n\nBut the word “realistic” matters. Cashflow assets does not get to skip the hard part. You have to ask what the income looks like after costs. You have to ask how stable it is. You have to ask how much attention it wants. You have to ask whether one surprise can wipe out six good months. You have to ask whether the income makes your life calmer or just adds another thing you have to manage.\n\nA lot of people chase cash flow because they want independence from a paycheck. That desire is understandable. Depending on one job can feel fragile, especially if your income, boss, industry, or health feels uncertain. Cash flow can create another leg under the table. But if the asset is weak, overleveraged, concentrated, or poorly understood, it might not reduce fragility. It might create a new version of it.\n\nThink about someone who buys a small asset that clears $300 a month in normal conditions. On paper, that sounds helpful. But what if the asset needs constant weekends? What if it creates arguments at home? What if it has surprise repairs? What if the income only works when the economy is hot? What if the person has no emergency fund, so every issue goes on a credit card? The income is real, but the freedom is not automatic.\n\nNow compare that with someone who has a simpler, steadier situation. Maybe the asset clears less, but it fits their life. It does not require constant emotional energy. It does not create panic when a small thing goes wrong. It has reserves. It is boring enough to keep. That might create more freedom than the higher income asset with constant drama.\n\nThe blind spot of this school is that cash flow can make people underestimate simplicity. Broad investing, career growth, or just lowering fixed expenses may create more freedom with less attention. A monthly check is not automatically better than a quiet plan.\n\nInside Nisk, the useful move is to test the realistic net income. Add the amount to a simulation. See if it changes your runway, timeline, or options in a meaningful way. Then ask the second question: what would this asset take from you to produce that cash? The goal is not income at any cost. The goal is more options than obligations.',
			flashcards: [
				{
					id: 'school-cashflow-assets-03-fc-1',
					role: 'main_idea',
					front: 'What is option income?',
					back: 'Recurring income that gives you more room, choices, or runway.',
				},
				{
					id: 'school-cashflow-assets-03-fc-2',
					role: 'watch_out',
					front: 'What is false freedom?',
					back: 'Income that looks good but costs too much time, stress, or risk.',
				},
				{
					id: 'school-cashflow-assets-03-fc-3',
					role: 'use_it',
					front: 'How do you test the lens?',
					back: 'Simulate realistic net income and ask what it changes in your life.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate extra monthly cash',
			},
			revision: 1,
		},
	],
};
